import React, { useCallback, useContext, useEffect, useRef, useState, useMemo } from "react"
import "react-dates/initialize"
import "react-dates/lib/css/_datepicker.css"
import { DayPickerRangeController } from "react-dates"
import { isInclusivelyBeforeDay } from "react-dates"
import { HORIZONTAL_ORIENTATION } from "react-dates/src/constants"
import moment, { Moment } from "moment"
import Holidays from "date-holidays"
import { displayDates } from "../../utils"
import { CampingStyleProps, CampProps, PriceProps } from "../../interfaces"
import { BookerContext } from "../../contexts/BookerContext"
import api from "../../api2"
import classNames from "../../utils/classNames"
import * as Sentry from "@sentry/react"
import { DateTime } from "luxon"
import CampDecorator from "../../decorators/CampDecorator"
import weekday from "../../types/weekday"
import IDateConfiguration from "../../interfaces/IDateConfiguration"
import HoverTip from "../ui/HoverTip"
import PortalHoverTip from "../ui/PortalHoverTip"
import Spinner from "../ui/Spinner"
import { hasHourlyOrDailyIntervals } from "./DatesCard"

const initDateConfiguration: IDateConfiguration = {
  start_mm_dd: null,
  end_mm_dd: null,
  start_type: null,
  end_type: null,
  minimum_nights: null,
  is_minimum_inside_range: false,
  minimum_weekend_nights: null,
  restricted_check_in_days: [],
  restricted_check_out_days: [],
}

const mapWeekendHolidayToDateConfiguration = (day: Moment, minimum_nights: number): IDateConfiguration => {
  if (day.weekday() == 1) {
    // Monday
    return {
      ...initDateConfiguration,
      start_mm_dd: moment(day).subtract(3, "days").format("MM-DD"),
      end_mm_dd: moment(day).add(1, "day").format("MM-DD"),
      minimum_nights,
    }
  } else if (day.weekday() == 2) {
    // Tuesday
    return {
      ...initDateConfiguration,
      start_mm_dd: moment(day).subtract(4, "days").format("MM-DD"),
      end_mm_dd: moment(day).format("MM-DD"),
      minimum_nights,
    }
  } else if (day.weekday() == 3) {
    // Wednesday
    return {
      ...initDateConfiguration,
      start_mm_dd: moment(day).format("MM-DD"),
      end_mm_dd: moment(day).add(4, "day").format("MM-DD"),
      minimum_nights,
    }
  } else if (day.weekday() == 4) {
    // Thursday
    return {
      ...initDateConfiguration,
      start_mm_dd: moment(day).format("MM-DD"),
      end_mm_dd: moment(day).add(3, "day").format("MM-DD"),
      minimum_nights,
    }
  } else if (day.weekday() == 5) {
    // Friday
    return {
      ...initDateConfiguration,
      start_mm_dd: moment(day).format("MM-DD"),
      end_mm_dd: moment(day).add(2, "day").format("MM-DD"),
      minimum_nights,
    }
  } else if (day.weekday() == 6) {
    // Saturday
    return {
      ...initDateConfiguration,
      start_mm_dd: moment(day).subtract(1, "day").format("MM-DD"),
      end_mm_dd: moment(day).add(2, "day").format("MM-DD"),
      minimum_nights,
    }
  } else {
    // Sunday
    return {
      ...initDateConfiguration,
      start_mm_dd: moment(day).subtract(2, "days").format("MM-DD"),
      end_mm_dd: moment(day).add(1, "day").format("MM-DD"),
      minimum_nights,
    }
  }
}

const rangeStartAndEndForConfig = (config: IDateConfiguration, year: number): [Moment, Moment] => {
  let rangeStart: Moment
  let rangeEnd: Moment

  if (config?.start_type === "holiday") {
    const holidays = new Holidays("US")
    rangeStart = moment(holidays.getHolidays(year).find((h) => h.name === config.start_mm_dd).date)
  } else {
    rangeStart = moment(config.start_mm_dd, "MM-DD").year(year)
  }

  if (config?.end_type === "holiday") {
    const holidays = new Holidays("US")
    rangeEnd = moment(holidays.getHolidays(year).find((h) => h.name === config.end_mm_dd).date)
  } else {
    rangeEnd = moment(config.end_mm_dd, "MM-DD").year(year)
  }

  return [rangeStart, rangeEnd]
}

const isDateWithinConfig = (date: Moment, config: IDateConfiguration, year: number): boolean => {
  const [rangeStart, rangeEnd] = rangeStartAndEndForConfig(config, year)
  return date.isBetween(rangeStart, rangeEnd, "day", "[]")
}

const preprocessIntoHashMap = (seasonalPrices: PriceProps[], viewingMonth: Moment): Map<string, PriceProps> => {
  const hashMap = new Map<string, PriceProps>()

  const years = [moment().year(), moment().year() + 1, moment().year() + 2]

  years.forEach((currentYear) => {
    seasonalPrices.forEach((price) => {
      let rangeStart = moment(price.range_start, "MM-DD").year(currentYear)
      let rangeEnd = moment(price.range_end, "MM-DD").year(currentYear)

      if (rangeStart.isAfter(rangeEnd)) {
        const viewingMonth = moment()
        viewingMonth.year() > moment().year() ? rangeStart.subtract(1, "y") : rangeEnd.add(1, "y")
      }

      const rangeDuration = rangeEnd.diff(rangeStart, "days")

      for (let m = moment(rangeStart); m.isSameOrBefore(rangeEnd); m.add(1, "day")) {
        const formattedDate = m.format("YYYY-MM-DD")

        if (hashMap.has(formattedDate)) {
          const existingPrice = hashMap.get(formattedDate)
          const existingRangeStart = moment(existingPrice.range_start, "MM-DD").year(currentYear)
          const existingRangeEnd = moment(existingPrice.range_end, "MM-DD").year(currentYear)
          const existingRangeDuration = existingRangeEnd.diff(existingRangeStart, "days")

          if (rangeDuration < existingRangeDuration) {
            hashMap.set(formattedDate, price)
          }
        } else {
          hashMap.set(formattedDate, price)
        }
      }
    })
  })

  return hashMap
}

const preprocessConfigurationsIntoHashMap = (
  dateConfigurations: IDateConfiguration[],
  currentViewingMonth: Moment,
): Map<string, IDateConfiguration> => {
  const hashMap = new Map<string, IDateConfiguration>()
  if (!dateConfigurations) return hashMap

  // NOTE: preprocesses date configurations for multiple years
  const years = [
    currentViewingMonth,
    moment(currentViewingMonth).add(1, "year"),
    moment(currentViewingMonth).add(2, "years"),
  ]

  years.forEach((viewingMonth) => {
    dateConfigurations.forEach((configuration) => {
      if (configuration.start_mm_dd && configuration.end_mm_dd) {
        let rangeStart: Moment
        let rangeEnd: Moment

        if (configuration?.start_type === "holiday") {
          const holidays = new Holidays("US")
          rangeStart = moment(
            holidays.getHolidays(viewingMonth.year()).find((h) => h.name === configuration.start_mm_dd).date,
          )
        } else {
          rangeStart = moment(configuration.start_mm_dd, "MM-DD").year(viewingMonth.year())
        }

        if (configuration?.end_type === "holiday") {
          const holidays = new Holidays("US")
          rangeEnd = moment(
            holidays.getHolidays(viewingMonth.year()).find((h) => h.name === configuration.end_mm_dd).date,
          )
        } else {
          rangeEnd = moment(configuration.end_mm_dd, "MM-DD").year(viewingMonth.year())
        }

        if (rangeStart.isAfter(rangeEnd)) {
          viewingMonth.year() > moment().year() ? rangeStart.subtract(1, "y") : rangeEnd.add(1, "y")
        }

        if (configuration.minimum_nights > 1 && !configuration.is_minimum_inside_range) {
          rangeEnd = moment(rangeEnd).add(configuration.minimum_nights - 1, "d")
        }

        for (let m = moment(rangeStart); m.isSameOrBefore(rangeEnd); m.add(1, "day")) {
          const formattedDate = m.format("YYYY-MM-DD")
          hashMap.set(formattedDate, { ...configuration })
        }
      } else {
        const startOfYear = moment(viewingMonth).startOf("year")
        const endOfYear = moment(viewingMonth).endOf("year")
        for (let m = moment(startOfYear); m.isSameOrBefore(endOfYear); m.add(1, "day")) {
          const formattedDate = m.format("YYYY-MM-DD")
          hashMap.set(formattedDate, {
            ...configuration,
            start_mm_dd: startOfYear.format("MM-DD"),
            end_mm_dd: endOfYear.format("MM-DD"),
            start_type: "fixed",
            end_type: "fixed",
          })
        }
      }
    })
  })

  return hashMap
}

interface ComponentProps {
  camp: CampProps
  visible: boolean
  startDate: Moment
  endDate: Moment | null
  campingStyle: CampingStyleProps
  minimumNights: number
}

const DatePicker = (props: ComponentProps) => {
  const [currentMonth, setCurrentMonth] = useState<Moment>(moment())
  const [monthRange, setMonthRange] = useState<{ from: moment.Moment; until: moment.Moment }>({
    from: currentMonth.clone(),
    until: currentMonth.clone().add(3, "months"),
  })
  const [focusedInput, setFocusedInput] = useState<"startDate" | "endDate">(props.startDate && !props.endDate ? "endDate" : "startDate")
  const [disableMinNights, setDisableMinNights] = useState(false)

  const [areDatesLoaded, setAreDatesLoaded] = useState<boolean>(false)

  const [isNarrowScreen, setIsNarrowScreen] = useState(window.outerWidth <= 675)
  const datesRequestCountRef = useRef(0)
  const initialRenderRef = useRef(true)

  useEffect(() => {
    const handleResize = () => {
      setIsNarrowScreen(window.outerWidth <= 675)
    }

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [])

  const {
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    camp,
    campingStyle,
    campingStylePrices,
    campingStyleDateConfigurations,
    selectedInterval,
    isDailyOrHourly,
    unavailableDates,
    setUnavailableDates,
    unavailableIntervalDates,
    setUnavailableIntervalDates,
  } = useContext(BookerContext)
  const seasonalPrices = campingStylePrices.filter((p) => p.range_start && p.range_end)
  const preprocessedSeasonalPrices = preprocessIntoHashMap(seasonalPrices, currentMonth)
  const preprocessedDateConfigurations = preprocessConfigurationsIntoHashMap(
    campingStyleDateConfigurations,
    currentMonth,
  )

  const holidayWeekendsCache = new Map<number, Map<string, IDateConfiguration>>()

  const holidayWeekendsFor = (camp: CampProps, year: number): Map<string, IDateConfiguration> => {
    if (holidayWeekendsCache.has(year)) {
      return holidayWeekendsCache.get(year)!
    }

    const holidays = new Holidays(camp.country)
    const hashMap = new Map<string, IDateConfiguration>()

    if (!camp.minimum_holiday_weekend_nights) return hashMap

    const observedHolidayNames =
      camp.country === "CA"
        ? ["Victoria Day", "Canada Day", "Civic Holiday", "Labour Day"]
        : ["Memorial Day", "Independence Day", "Labor Day", "Columbus Day"]
    const observedHolidays = holidays.getHolidays(year).filter((h) => observedHolidayNames.includes(h.name))

    observedHolidays.forEach((holiday) => {
      const drc = mapWeekendHolidayToDateConfiguration(moment(holiday.date), camp.minimum_holiday_weekend_nights)
      const rangeStart = moment(drc.start_mm_dd, "MM-DD").year(moment(holiday.date).year())
      const rangeEnd = moment(drc.end_mm_dd, "MM-DD").year(moment(holiday.date).year())

      for (let m = moment(rangeStart); m.isSameOrBefore(rangeEnd); m.add(1, "day")) {
        const formattedDate = m.format("YYYY-MM-DD")
        hashMap.set(formattedDate, drc)
      }
    })

    holidayWeekendsCache.set(year, hashMap)

    return hashMap
  }

  const weekendsCache = new Map<number, Map<string, IDateConfiguration>>()

  const weekendsFor = (
    camp: CampProps,
    year: number,
    dateConfigurations: IDateConfiguration[],
  ): Map<string, IDateConfiguration> => {
    if (weekendsCache.has(year)) {
      return weekendsCache.get(year)!
    }

    const hashMap = new Map<string, IDateConfiguration>()
    const weekendDateConfigurations = dateConfigurations.filter((dc) => dc.minimum_weekend_nights)

    if (!camp.minimum_weekend_nights && !weekendDateConfigurations.length) {
      return hashMap
    }

    const startOfYear = moment().year(year).startOf("year")
    const endOfYear = moment().year(year).endOf("year")

    for (let m = moment(startOfYear); m.isSameOrBefore(endOfYear); m.add(1, "day")) {
      if (m.weekday() !== 5) continue

      const dateConfig = weekendDateConfigurations.find((dc) => isDateWithinConfig(m, dc, year))
      const minimumNights = dateConfig?.minimum_weekend_nights || camp.minimum_weekend_nights

      if (!minimumNights) continue

      const drc: IDateConfiguration = {
        ...initDateConfiguration,
        start_mm_dd: m.format("MM-DD"),
        end_mm_dd: m.clone().add(2, "day").format("MM-DD"),
        start_type: "fixed",
        end_type: "fixed",
        minimum_nights: minimumNights,
      }

      const [rangeStart, rangeEnd] = rangeStartAndEndForConfig(drc, year)

      for (let n = moment(rangeStart); n.isSameOrBefore(rangeEnd); n.add(1, "day")) {
        hashMap.set(n.format("YYYY-MM-DD"), drc)
      }
    }

    weekendsCache.set(year, hashMap)

    return hashMap
  }

  useEffect(() => {
    const initialMonth = calculateInitialVisibleMonth()
    setCurrentMonth(initialMonth)

    setMonthRange({
      from: initialMonth.clone().startOf("month"),
      until: initialMonth.clone().add(4, "months"),
    })
  }, [])

  useEffect(() => {
    if (initialRenderRef.current) {
      initialRenderRef.current = false
      return
    }

    let from = currentMonth.clone().startOf("month").utc(true).toISOString()

    const visibleMonthCount = !isNarrowScreen ? 2 : 4

    let until = currentMonth
      .clone()
      .add(visibleMonthCount - 1, "months")
      .endOf("month")
      .utc(true)
      .toISOString()

    setAreDatesLoaded(false)
    datesRequestCountRef.current += 1

    api.DateAvailabilities.forDates({
      campId: camp.id,
      campingStyleId: campingStyle.id,
      bookingIntervalId: selectedInterval?.id,
      startDate: from,
      endDate: until,
    })
      .then(({ unavailable_dates, unavailable_interval_dates }) => {
        setUnavailableDates({})
        setUnavailableIntervalDates({})
        if (unavailable_interval_dates) {
          const intervalDatesByMonth = unavailable_interval_dates.reduce((acc, dateObj) => {
            const month = moment(dateObj.date).format("YYYY-MM")
            acc[month] = acc[month] || {}
            acc[month][dateObj.date] = dateObj.intervals.map((i) => i.id)
            return acc
          }, {})
          setUnavailableIntervalDates(intervalDatesByMonth)
        } else if (unavailable_dates) {
          const datesByMonth = unavailable_dates.reduce((acc, date) => {
            const month = moment(date).format("YYYY-MM")
            acc[month] = acc[month] || []
            acc[month].push(date)
            return acc
          }, {})
          setUnavailableDates(datesByMonth)
        }
        datesRequestCountRef.current -= 1
        if (datesRequestCountRef.current === 0) {
          setAreDatesLoaded(true)
        }
      })
      .catch((err) => {
        datesRequestCountRef.current -= 1
        if (datesRequestCountRef.current === 0) {
          setAreDatesLoaded(true)
        }
        console.error(err)
        Sentry.captureException(err)
      })
  }, [currentMonth, campingStyle, isNarrowScreen])

  useEffect(() => {
    if (!startDate) {
      setFocusedInput("startDate")
    }
  }, [startDate])

  const onChange = (dates: { startDate: Moment; endDate: Moment }) => {
    if (
      dates.endDate &&
      dates.startDate &&
      moment(dates.endDate).startOf("day").isBefore(moment(dates.startDate).startOf("day"))
    ) {
      return
    }

    const checkinTime = campingStyle.checkin_time || "12:00"
    const checkoutTime = campingStyle.checkout_time || "12:00"
    const [checkoutHour, checkoutMinute] = checkoutTime.split(":").map(Number)
    const [checkinHour, checkinMinute] = checkinTime.split(":").map(Number)
    let start: Moment = dates.startDate?.hour(checkinHour)?.minute(checkinMinute)
    let end: Moment = dates.endDate?.hour(checkoutHour)?.minute(checkoutMinute)

    if (selectedInterval) {
      const startHour = Number(selectedInterval.start_time.split(":")[0])
      const endHour = Number(selectedInterval.end_time.split(":")[0])
      start = dates.startDate?.hour(startHour)
      end = dates.endDate?.hour(endHour)
    }

    setStartDate(start)
    setEndDate(end)

    const isHourlyBooking = hasHourlyOrDailyIntervals(campingStyle)
    setDisableMinNights(isHourlyBooking)
  }

  const onFocusChange = (input: "startDate" | "endDate") => {
    if (input) {
      if (input == "endDate") {
        setEndDate(null)
      }
      setFocusedInput(input)
    } else {
      setFocusedInput("startDate")
    }
  }

  const onClickClearDates = () => {
    setStartDate(null)
    setEndDate(null)
    setFocusedInput("startDate")
  }

  const isOutsideSeason = (day: Moment, seasonStartDate: Moment, seasonEndDate: Moment) => {
    if (!(seasonStartDate && seasonEndDate)) return false

    const seasonStart: Moment = moment(seasonStartDate).hour(12)?.utc(true)
    const seasonEnd: Moment = moment(seasonEndDate)?.endOf("day")?.utc(true)

    return day.dayOfYear() < seasonStart.dayOfYear() || day.startOf("day").dayOfYear() > seasonEnd.dayOfYear()
  }

  const queryObject: { t?: string } = decodeURIComponent(window.location.search)
    .split("?")
    .pop()
    .split("&")
    .reduce((obj, x) => {
      const [key, value] = x.split("=")
      obj[key] = value
      return obj
    }, {})

  const bypassUnpublishedFlag = queryObject.t == "bypass"

  const isOutsideBookableDays = (day: Moment, bookingStart: Moment, seasonEnd: Moment) => {
    if (!bookingStart || bypassUnpublishedFlag) return false

    const todayIsBeforeBookingStart = moment().isBefore(bookingStart)
    const dayIsAfterSeasonEnd = seasonEnd
      ? day.isAfter(seasonEnd, "days")
      : day.isAfter(moment(bookingStart).endOf("year"), "days")

    return todayIsBeforeBookingStart || dayIsAfterSeasonEnd
  }

  const dateRangeConfigurationFor = (day: Moment): IDateConfiguration => {
    let formattedDay = day.format("YYYY-MM-DD")

    if (window.outerWidth < 675) {
      formattedDay = `${currentMonth.format("YYYY")}-${day.format("MM-DD")}`
    }

    const year = day.year()

    const preprocessedHolidayWeekends = holidayWeekendsFor(camp, year)
    const preprocessedWeekends = weekendsFor(camp, year, campingStyleDateConfigurations)

    const seasonalPrice = preprocessedSeasonalPrices.get(formattedDay)
    const holidayWeekend = preprocessedHolidayWeekends.get(formattedDay)
    const weekend = preprocessedWeekends.get(formattedDay)
    const config = preprocessedDateConfigurations.get(formattedDay)

    let dateRangeConfiguration: IDateConfiguration
    let isWeekend = false

    if (holidayWeekend) {
      dateRangeConfiguration = holidayWeekend
    } else if (weekend) {
      dateRangeConfiguration = weekend
      isWeekend = true
    } else if (config) {
      dateRangeConfiguration = config
    } else if (seasonalPrice && seasonalPrice.minimum_nights) {
      // NOTE: this is deprecated and should be removed
      console.log("DEPRECATED: seasonalPrice.minimum_nights")
      Sentry.captureMessage("DEPRECATED: seasonalPrice.minimum_nights in DatePicker for camp " + camp.slug)
      dateRangeConfiguration = {
        ...initDateConfiguration,
        start_mm_dd: seasonalPrice.range_start,
        end_mm_dd: seasonalPrice.range_end,
        start_type: seasonalPrice.range_end_type,
        end_type: seasonalPrice.range_end_type,
        minimum_nights: seasonalPrice.minimum_nights,
      }
    } else {
      return null
    }

    const nDay = moment(day).hour(0).minute(0).utc(true)
    let rangeStart = moment(dateRangeConfiguration.start_mm_dd, "MM-DD").year(day.year()).hour(0).minute(0).utc(true)

    let rangeEnd = moment(dateRangeConfiguration.end_mm_dd, "MM-DD").year(day.year()).hour(0).minute(0).utc(true)

    if (rangeStart.isAfter(rangeEnd)) {
      if (day.year() > moment().year()) {
        rangeStart = rangeStart.subtract(1, "year")
      } else {
        rangeEnd = rangeEnd.add(1, "year")
      }
    }
    const isEndOfRangeTooShort =
      (!startDate || nDay.isBefore(startDate)) &&
      !isWeekend &&
      dateRangeConfiguration.is_minimum_inside_range &&
      nDay.isBetween(moment(rangeEnd).subtract(dateRangeConfiguration.minimum_nights, "d"), rangeEnd, "day", "()")
    let wouldStartOfRangeBeTooShort = false
    if (startDate) {
      const selectedStartDate = moment(startDate)
      if (selectedStartDate.isBefore(rangeStart)) {
        let endOfMinimumNightsRestriction: Moment
        if (dateRangeConfiguration.is_minimum_inside_range) {
          endOfMinimumNightsRestriction = moment(rangeStart)
            .add(dateRangeConfiguration.minimum_nights, "d")
            .add(12, "h")
        } else {
          endOfMinimumNightsRestriction = moment(selectedStartDate).add(dateRangeConfiguration.minimum_nights, "d")
        }
        wouldStartOfRangeBeTooShort =
          startDate.isBefore(rangeEnd) && nDay.isBetween(moment(rangeStart), endOfMinimumNightsRestriction, "day", "()")
      } else {
        wouldStartOfRangeBeTooShort =
          startDate.isBefore(rangeEnd) &&
          nDay.isBetween(
            moment(selectedStartDate),
            moment(selectedStartDate).add(dateRangeConfiguration.minimum_nights, "d"),
            "day",
            "()",
          )
      }
    }

    if (isEndOfRangeTooShort || wouldStartOfRangeBeTooShort) {
      return dateRangeConfiguration
    } else {
      return null
    }
  }

  const isOutsideRange = (dayInput: Moment) => {
    const day = moment(dayInput).hour(12)?.utc(true)
    const today = moment()
    if (focusedInput === "endDate" && startDate && day.isAfter(moment(startDate).add(6, "months"), "day")) {
      return true
    }
    const formattedDay = moment(dayInput).format("YYYY-MM-DD")
    const flattenedUnavailableDates = Object.values(unavailableDates).flatMap((d) => d)
    const flattenedUnavailableIntervalDates = Object.values(unavailableIntervalDates).reduce(
      (acc, val) => {
        Object.keys(val).forEach((key) => {
          acc[key] ||= []
          acc[key].push(...val[key])
        })
        return acc
      },
      {} as Record<string, string[]>,
    )

    const [restrictedCheckInDays, restrictedCheckOutDays] = restricted_days_for(day)
    const onlyStartSelected = startDate && (!endDate || startDate.isSame(endDate, "day"))

    if (campingStyle?.booking_intervals?.length) {
      // TODO: review and clean up logs
      const noIntervalsAvailableForDay =
        flattenedUnavailableIntervalDates[formattedDay] &&
        (flattenedUnavailableIntervalDates[formattedDay].length >= campingStyle.booking_intervals.length ||
          (selectedInterval && flattenedUnavailableIntervalDates[formattedDay].includes(selectedInterval.id)))

      const isBetweenRestrictedAndUnavailable =
        flattenedUnavailableDates.includes(moment(formattedDay).add(1, "day").format("YYYY-MM-DD")) &&
        restrictedCheckInDays &&
        restrictedCheckInDays.includes((day.weekday() - 1) % 7)

      if (!onlyStartSelected && noIntervalsAvailableForDay) {
        return true
      } else if (isBetweenRestrictedAndUnavailable) {
        return true
      } else if (onlyStartSelected && Object.entries(flattenedUnavailableIntervalDates).length) {
        const sortedEntries = Object.entries(flattenedUnavailableIntervalDates).sort(
          ([aDate, aIntervals], [bDate, bIntervals]) => (aDate > bDate ? 1 : -1),
        )
        const firstEntry = sortedEntries.find(([entryDate, entryIntervalIds]) => {
          const isBefore = startDate.clone().startOf("day").isBefore(moment(entryDate).clone().startOf("day"))
          return (
            isBefore &&
            ((selectedInterval && entryIntervalIds.includes(selectedInterval.id)) ||
              entryIntervalIds.length >= campingStyle.booking_intervals.length)
          )
        })
        let unavailableDayAfterStart: Moment
        if (firstEntry) {
          unavailableDayAfterStart = moment(firstEntry[0]).subtract(1, "day")
        }

        if (unavailableDayAfterStart && dayInput.isAfter(unavailableDayAfterStart, "day")) {
          return true
        }
      }
    } else {
      const isBetweenRestrictedAndUnavailable =
        flattenedUnavailableDates.includes(moment(formattedDay).add(1, "day").format("YYYY-MM-DD")) &&
        restrictedCheckInDays &&
        restrictedCheckInDays.includes((day.weekday() - 1) % 7)

      const isValidCheckoutDay =
        (focusedInput === "endDate" &&
          startDate &&
          moment(formattedDay).startOf("day").isAfter(moment(startDate).startOf("day"))) ||
        (endDate &&
          startDate &&
          moment(startDate).add(1, "day").format("YYYY-MM-DD") === formattedDay &&
          moment(endDate).format("YYYY-MM-DD") === formattedDay)

      if (
        (flattenedUnavailableDates.includes(formattedDay) && !isValidCheckoutDay) ||
        isBetweenRestrictedAndUnavailable
      ) {
        return true
      } else if (onlyStartSelected && flattenedUnavailableDates.length) {
        const unavailableDateAfterStart = flattenedUnavailableDates
          .sort((a, b) => (a > b ? 1 : -1))
          .find((d) => startDate.isBefore(d, "day"))
        if (unavailableDateAfterStart && dayInput.isAfter(unavailableDateAfterStart, "day")) {
          return true
        }
      }
    }

    let bookable_for_n_days_options = [props.camp.bookable_for_n_days, campingStyle?.bookable_for_n_days].filter(
      (n) => n !== null && n !== undefined && n > 0,
    )

    const nextBookableDays = Math.min(...bookable_for_n_days_options)

    const isBeforeToday = isInclusivelyBeforeDay(day, moment().subtract(1, "d"))
    const isToday = today.isSame(day, "d")
    let canCheckInSameDay =
      campingStyle.allow_same_day_reservations === null
        ? camp.allow_same_day_reservations
        : campingStyle.allow_same_day_reservations
    const cutoffTime =
      campingStyle.allow_same_day_reservations === null
        ? camp.same_day_reservations_cutoff_time
        : campingStyle.same_day_reservations_cutoff_time

    if (canCheckInSameDay && cutoffTime) {
      const [cutoffHour, cutoffMinute] = cutoffTime.split(":").map((t) => Number(t))

      const allowedCheckinTime = DateTime.now()
        .setZone(props.camp.timezone)
        .set({ hour: cutoffHour, minute: cutoffMinute })

      if (allowedCheckinTime.diffNow("seconds").seconds < 0) {
        canCheckInSameDay = false
      }
    }

    // TODO: replace all camp references with decoratedCamp via API
    const decoratedCamp = new CampDecorator(camp)

    const seasonStart = camp.season_start_mm_dd && moment(camp.season_start_mm_dd, "MM-DD").year(day.year())
    const seasonEnd = camp.season_end_mm_dd && moment(camp.season_end_mm_dd, "MM-DD").year(day.year())
    const bookingStart =
      decoratedCamp.bookingStartDatetime &&
      moment(decoratedCamp.bookingStartDatetime.set({ year: day.year() }).toJSDate())

    return (
      isBeforeToday ||
      (isToday && !canCheckInSameDay) ||
      isOutsideSeason(day, seasonStart, seasonEnd) ||
      isOutsideBookableDays(day, bookingStart, seasonEnd) ||
      (nextBookableDays && day.isAfter(moment(today).add(nextBookableDays, "d")))
    )
  }

  const isDayRestrictedFor = (day: Moment, sDate: Moment, eDate: Moment) => {
    const [restrictedCheckInDays, restrictedCheckOutDays] = restricted_days_for(day)

    if (
      (!sDate || (sDate && eDate) || (sDate && day.isBefore(sDate))) &&
      restrictedCheckInDays &&
      restrictedCheckInDays.includes(day.weekday())
    ) {
      return true
    }

    if (sDate && !eDate && restrictedCheckOutDays && restrictedCheckOutDays.includes(day.weekday())) {
      return true
    }

    return false
  }

  const restricted_days_for = (day: Moment): [number[], number[]] => {
    const config = preprocessedDateConfigurations.get(day.format("YYYY-MM-DD"))
    const restrictedCheckInDays = mapWeekdaysToNumbers(
      config?.restricted_check_in_days || campingStyle.restricted_check_in_days,
    )
    const restrictedCheckOutDays = mapWeekdaysToNumbers(
      config?.restricted_check_out_days || campingStyle.restricted_check_out_days,
    )

    return [restrictedCheckInDays, restrictedCheckOutDays]
  }

  const calculateInitialVisibleMonth = (): Moment => {
    if (startDate) return startDate

    const now = moment()

    let seasonStart = camp.season_start_mm_dd && moment(camp.season_start_mm_dd, "MM-DD").year(now.year())
    const seasonEnd = camp.season_end_mm_dd && moment(camp.season_end_mm_dd, "MM-DD").year(now.year())
    if (seasonEnd && now.isAfter(seasonEnd)) {
      seasonStart = seasonStart.year(now.year() + 1)
    }

    if (seasonStart && now.isBefore(seasonStart)) {
      return seasonStart
    } else {
      return now
    }
  }

  const handleRenderDayContents = (day: Moment) => {
    const dateRangeConfiguration = dateRangeConfigurationFor(day)
    let minimumNights: number
    const maximumNights: number = camp.max_stay_nights || campingStyle.max_stay_nights
    if (dateRangeConfiguration) {
      minimumNights = dateRangeConfiguration.minimum_nights
    } else if (startDate && (!endDate || startDate.isSame(endDate, "d"))) {
      if (!!campingStyle.minimum_bookable_nights) {
        const from = moment(startDate)
        const until = moment(startDate).add(campingStyle.minimum_bookable_nights, "d")
        if (day.isBetween(from, until, "day", "[)")) {
          minimumNights = campingStyle.minimum_bookable_nights
        }
      }
    }

    const beyondMaximumNumberOfDays =
      !!maximumNights &&
      startDate?.isValid() &&
      (!endDate || startDate.isSame(endDate, "d")) &&
      day.isAfter(moment(startDate).add(maximumNights, "d"), "day")

    if (isDayRestrictedFor(day, startDate, endDate)) {
      return (
        <PortalHoverTip
          content={`Not available for ${startDate && (!endDate || startDate.isSame(endDate, "d")) && day.isAfter(startDate) ? "check-out" : "check-in"}`}
        >
          <div className="">{day.format("D")}</div>
        </PortalHoverTip>
      )
    } else if (beyondMaximumNumberOfDays) {
      return (
        <PortalHoverTip
          content={
            props.campingStyle.max_stay_nights_message
              ? props.campingStyle.max_stay_nights_message
                  .replace("%%", maximumNights.toString())
                  .replace("##", props.camp.phone || "our phone")
              : `${maximumNights}-night maximum`
          }
        >
          <div className="">{day.format("D")}</div>
        </PortalHoverTip>
      )
    } else if (minimumNights && minimumNights > 1) {
      let minimumEndDateStr: string

      if (dateRangeConfiguration) {
        if (dateRangeConfiguration.end_type == "holiday") {
          minimumEndDateStr = dateRangeConfiguration.end_mm_dd
        } else {
          minimumEndDateStr = moment(dateRangeConfiguration.end_mm_dd, "MM-DD").format("MMM D")
        }
      }

      return (
        <PortalHoverTip
          content={
            (startDate && !day.isBefore(startDate)) || !dateRangeConfiguration?.is_minimum_inside_range
              ? `${minimumNights}-night minimum`
              : `${minimumNights}-night minimum until ${minimumEndDateStr}`
          }
        >
          <div className="">{day.format("D")}</div>
        </PortalHoverTip>
      )
    } else {
      return (
        <>
          <div className="">{day.format("D")}</div>
        </>
      )
    }
  }

  const mapWeekdaysToNumbers = (weekdays: Array<weekday>) => {
    return weekdays.map((day) => {
      return moment().day(day).isoWeekday() % 7
    })
  }

  const handleIsDayBlocked = (day: Moment) => {
    if (!areDatesLoaded || isDayRestrictedFor(day, startDate, endDate)) return true

    const seasonalMinimumNightsForDay = dateRangeConfigurationFor(day)?.minimum_nights

    const isBeforeMinimumStay = Number(seasonalMinimumNightsForDay) > 1
    const maxStayNights = camp.max_stay_nights || campingStyle.max_stay_nights

    const isAfterMaxStay =
      startDate &&
      (!endDate || startDate.isSame(endDate, "d")) &&
      maxStayNights &&
      day.isAfter(moment(startDate).add(maxStayNights, "d"), "day")
    return isBeforeMinimumStay || isAfterMaxStay
  }

  const handleRenderNextButton = (args) => {
    const { onClick, disabled }: { onClick: () => any; disabled: boolean } = args

    if (window.outerWidth > 675) return null

    return (
      <button
        onClick={() => {
          const nextMonth = currentMonth.clone().add(4, "months")
          handleNextMonthClick(nextMonth)

          onClick()
        }}
        disabled={disabled}
        className="mt-8 w-full border border-gray-500 rounded-md py-2 text-gray-800 hover:text-gray-500 transition-colors duration-200 ease-in-out"
      >
        Load more dates
      </button>
    )
  }

  let headerText = ""
  let headerSubtext = ""
  // TODO: let's make sure this handles all edgecases with timezones/times of day
  const numberOfDays = moment(endDate).hour(0).minute(0).diff(moment(startDate).hour(0).minute(0), "days")

  if (!startDate) {
    headerText = "Select arrival date"
  } else if (startDate && !endDate) {
    headerText = "Select departure date"
  } else if (isDailyOrHourly) {
    headerText = numberOfDays + 1 + (numberOfDays + 1 > 1 ? " Days" : " Day")
  } else if (startDate && endDate) {
    headerText = numberOfDays + (numberOfDays > 1 ? " Nights" : " Night")
  }

  if (!startDate && !endDate) headerSubtext = "Add your travel dates"
  else if (startDate && !endDate && props.minimumNights > 1)
    headerSubtext = `Minimum stay: ${props.minimumNights} nights`
  else headerSubtext = displayDates(startDate, endDate)

  const mouseout = (overflowEl) => {
    setTimeout(() => {
      overflowEl[0]?.classList.add("day_picker_visible")
      overflowEl[0]?.classList.remove("day_picker_hidden")
    }, 1500)
  }

  const mouseover = (overflowEl) => {
    overflowEl[0]?.classList.add("day_picker_hidden")
    overflowEl[0]?.classList.remove("day_picker_visible")
  }

  const handleNextMonthClick = useCallback(
    (newMonth) => {
      const nextMonth = newMonth.clone()
      setCurrentMonth(nextMonth)

      setMonthRange({
        from: nextMonth.clone().startOf("month"),
        until: nextMonth.clone().add(4, "months"),
      })
    },
    [currentMonth, monthRange],
  )

  const handlePrevMonthClick = useCallback(
    (newMonth) => {
      const prevMonth = newMonth.clone()
      setCurrentMonth(prevMonth)

      setMonthRange({
        from: prevMonth.clone().startOf("month"),
        until: prevMonth.clone().add(4, "months"),
      })
    },
    [currentMonth, monthRange],
  )

  return (
    <>
      <div className={classNames(props.visible ? "flex-date-picker-container transition visible relative flex-col" : "transition hidden", isNarrowScreen && "flex-1 min-h-[48rem]")}>
        <div className="text-center mt-3 mb-6 lg:mb-0 ">
          <div className="text-2xl font-semibold">{headerText}</div>
          <p className="text-gray-600 text-base">{headerSubtext}</p>
          {startDate && !endDate && campingStyle?.max_stay_nights && campingStyle?.max_stay_nights_message && (
            <p className="text-gray-600 text-base">
              {campingStyle.max_stay_nights_message
                .replace("%%", campingStyle.max_stay_nights.toString())
                .replace("##", camp.phone || "our phone")}
            </p>
          )}
          <a
            onClick={onClickClearDates}
            className={classNames(
              "text-sm underline font-semibold hover:text-gray-700",
              !startDate && !endDate && "opacity-30 pointer-events-none",
            )}
          >
            Clear dates
          </a>
        </div>
        <div
          className={classNames("relative", !isNarrowScreen ? "flex h-96 justify-center w-fit" : "flex-1")}
          style={{ margin: !isNarrowScreen && "0 auto" }}
        >
          <DayPickerRangeController
            startDate={startDate}
            endDate={endDate}
            onDatesChange={onChange}
            focusedInput={focusedInput}
            onFocusChange={onFocusChange}
            noNavPrevButton={isNarrowScreen}
            renderNavNextButton={isNarrowScreen && handleRenderNextButton}
            onNextMonthClick={handleNextMonthClick}
            onPrevMonthClick={handlePrevMonthClick}
            orientation={!isNarrowScreen ? HORIZONTAL_ORIENTATION : "verticalScrollable"}
            isOutsideRange={isOutsideRange}
            isDayBlocked={handleIsDayBlocked}
            numberOfMonths={!isNarrowScreen ? 2 : 4}
            initialVisibleMonth={calculateInitialVisibleMonth}
            renderDayContents={handleRenderDayContents}
            noBorder
            hideKeyboardShortcutsPanel
            minimumNights={props.minimumNights}
            daysViolatingMinNightsCanBeClicked={disableMinNights}
          />
          {!areDatesLoaded && (
            <div className="absolute inset-0 flex items-center justify-center z-50 bg-white bg-opacity-80">
              <div className="flex flex-col items-center pt-8 pb-16">
                <div className="mb-4">
                  <Spinner size={7} />
                </div>
                <div className="text-gray-500 bg-white bg-opacity-100 rounded-md py-1.5 px-3">
                  <div className="h-full w-full flex items-center justify-center">
                    <span className="text-base text-gray-500 animate-pulse">Checking availability...</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default DatePicker
